import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";

const InstitutionSelector = ({
  onSelect,
  schoolType,
  required = false,
  className = "",
}) => {
  const [query, setQuery] = useState("");
  const [institutions, setInstitutions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState(null);
  const wrapperRef = useRef(null);

  // Fetch institutions based on search query and school type
  const searchInstitutions = async (searchQuery) => {
    try {
      setIsLoading(true);
      setError(null);

      let query = supabase
        .from("institutions")
        .select("name, type")
        .ilike("name", `%${searchQuery}%`);

      // Add school type filter if provided
      if (schoolType) {
        query = query.eq("type", schoolType);
      }

      // Limit results
      query = query.limit(10);

      const { data, error } = await query;

      if (error) throw error;
      setInstitutions(data);
    } catch (err) {
      setError("Failed to fetch institutions");
      console.error("Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.length >= 2) {
        searchInstitutions(query);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, schoolType]);

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
        placeholder="Search for your institution..."
        required={required}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
          focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${className}`}
      />

      {showDropdown && query.length >= 2 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading && (
            <div className="p-4 text-gray-500 text-center">Loading...</div>
          )}

          {error && <div className="p-4 text-red-500 text-center">{error}</div>}

          {!isLoading && !error && institutions.length === 0 && (
            <div className="p-4 text-gray-500 text-center">
              No institutions found
            </div>
          )}

          {!isLoading &&
            !error &&
            institutions.map((institution, index) => (
              <div
                key={index}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  setQuery(institution.name);
                  onSelect(institution);
                  setShowDropdown(false);
                }}
              >
                <div className="font-medium">{institution.name}</div>
                <div className="text-sm text-gray-500 capitalize">
                  {institution.type}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default InstitutionSelector;
